import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ChevronLeft, ChevronRight, Volume2, VolumeX, Sparkles, Moon, Stars, Globe, Users, Zap, Eye, Info, Brain, Palette, Circle } from 'lucide-react';
import { useAmbientSound } from '@/hooks/useAmbientSound';
import { useLanguage, LanguageToggle } from '@/contexts/LanguageContext';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Active Users Counter Component - Optimized
const ActiveUsersCounter = () => {
  const [activeCount, setActiveCount] = useState(0);
  const [isConnected, setIsConnected] = useState(false);
  const wsRef = useRef(null);
  const reconnectTimeoutRef = useRef(null);
  
  useEffect(() => {
    const connectWebSocket = () => {
      const wsUrl = API_URL.replace('https://', 'wss://').replace('http://', 'ws://');
      const ws = new WebSocket(`${wsUrl}/api/active/ws`);
      wsRef.current = ws;
      
      ws.onopen = () => setIsConnected(true);
      
      ws.onmessage = (event) => {
        try {
          const data = JSON.parse(event.data);
          if (data.type === 'active_count') {
            setActiveCount(data.count);
          } else if (data.type === 'ping') {
            ws.send(JSON.stringify({ action: 'pong' }));
          }
        } catch (e) {}
      };
      
      ws.onclose = () => {
        setIsConnected(false);
        reconnectTimeoutRef.current = setTimeout(connectWebSocket, 5000);
      };
      
      ws.onerror = () => ws.close();
    };
    
    connectWebSocket();
    
    return () => {
      if (wsRef.current) wsRef.current.close();
      if (reconnectTimeoutRef.current) clearTimeout(reconnectTimeoutRef.current);
    };
  }, []);
  
  return (
    <div className="glass-panel px-4 py-2 flex items-center gap-2">
      <div className={`w-2 h-2 rounded-full ${isConnected ? 'bg-green-400' : 'bg-slate-500'}`} />
      <Users className="w-4 h-4 text-cyan-400" />
      <span className="text-sm font-medium text-slate-200">
        <span className="text-cyan-400">{activeCount}</span>
        <span className="text-slate-400 ml-1">aktif</span>
      </span>
    </div>
  );
};
// Original Aura Ring Component - Optimized for performance
const AuraRing = React.memo(() => {
  return (
    <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
      {/* Outer breathing ring - static */}
      <div
        className="absolute w-[400px] h-[400px] md:w-[500px] md:h-[500px] rounded-full opacity-50"
        style={{
          background: 'conic-gradient(from 0deg, rgba(139, 92, 246, 0.3), rgba(6, 182, 212, 0.3), rgba(139, 92, 246, 0.1), rgba(168, 85, 247, 0.3), rgba(139, 92, 246, 0.3))',
          filter: 'blur(60px)',
        }}
      />
      
      {/* Middle ring - static */}
      <div
        className="absolute w-[280px] h-[280px] md:w-[350px] md:h-[350px] rounded-full border-2"
        style={{
          borderColor: 'rgba(139, 92, 246, 0.2)',
          boxShadow: '0 0 40px rgba(139, 92, 246, 0.3), inset 0 0 40px rgba(139, 92, 246, 0.1)',
        }}
      />
      
      {/* Inner glow ring - only this one animates */}
      <motion.div
        className="absolute w-[200px] h-[200px] md:w-[250px] md:h-[250px] rounded-full"
        style={{
          background: 'radial-gradient(circle, rgba(139, 92, 246, 0.2) 0%, transparent 70%)',
        }}
        animate={{ opacity: [0.5, 0.8, 0.5] }}
        transition={{ duration: 4, repeat: Infinity, ease: 'easeInOut' }}
      />
    </div>
  );
});

// Mystical floating particles - optimized
const MysticalParticles = React.memo(() => {
  // Reduced from 12 to 6 particles
  const particles = [
    { x: 15, y: 20, color: '#a78bfa' },
    { x: 75, y: 30, color: '#22d3ee' },
    { x: 25, y: 60, color: '#f472b6' },
    { x: 85, y: 70, color: '#a78bfa' },
    { x: 45, y: 85, color: '#22d3ee' },
    { x: 65, y: 15, color: '#f472b6' },
  ];
  
  return (
    <div className="absolute inset-0 overflow-hidden pointer-events-none">
      {particles.map((p, i) => (
        <motion.div
          key={i}
          className="absolute w-1 h-1 rounded-full"
          style={{
            left: `${p.x}%`,
            top: `${p.y}%`,
            backgroundColor: p.color,
            boxShadow: `0 0 8px ${p.color}`,
          }}
          animate={{ opacity: [0.3, 0.7, 0.3] }}
          transition={{ duration: 4, repeat: Infinity, delay: i * 0.5, ease: 'easeInOut' }}
        />
      ))}
    </div>
  );
});

// Sound Toggle Component with 288 Hz info
const SoundToggle = ({ isMuted, onToggle }) => {
  const [showInfo, setShowInfo] = useState(false);
  
  return (
    <div className="fixed top-6 left-6 z-50 flex items-center gap-2">
      <button
        onClick={onToggle}
        className="flex items-center gap-2 glass-panel px-3 py-2 text-sm text-slate-300 hover:text-white transition-all hover:border-violet-500/50"
        data-testid="sound-toggle"
      >
        {isMuted ? <VolumeX className="w-4 h-4" /> : <Volume2 className="w-4 h-4 text-violet-400" />}
        <span className="text-xs text-cyan-400 font-medium">288 Hz</span>
      </button>
      
      {/* Info button */}
      <button
        onClick={() => setShowInfo(!showInfo)}
        className="glass-panel p-2 text-slate-400 hover:text-cyan-400 transition-all"
        title="288 Hz hakkında"
      >
        <Info className="w-3 h-3" />
      </button>
      
      {/* Info tooltip */}
      <AnimatePresence>
        {showInfo && (
          <motion.div
            initial={{ opacity: 0, y: -10, scale: 0.95 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: -10, scale: 0.95 }}
            className="absolute top-full left-0 mt-2 w-72 glass-panel p-4 space-y-2 border-cyan-500/20"
          >
            <h4 className="text-sm font-medium text-cyan-400 flex items-center gap-2">
              <Eye className="w-4 h-4" />
              288 Hz - Üçüncü Göz Frekansı
            </h4>
            <p className="text-xs text-slate-400 leading-relaxed">
              288 Hz, Ajna (üçüncü göz) çakrasını uyandırmak için kullanılan özel bir frekanstır. 
              Bu frekans, pineal bezi aktive eder ve sezgisel algıyı güçlendirir.
            </p>
            <p className="text-xs text-slate-500 leading-relaxed">
              Meditasyon ve psişik çalışmalarda yaygın olarak kullanılır. Zihni sakinleştirir 
              ve içsel görüşü açar.
            </p>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

// Telepathy Mode Banner - Now Active!
const TelepathyBanner = ({ onOpenTelepathy }) => {
  const { language } = useLanguage();
  const connectionPoints = [
    { x: 20, y: 30 }, { x: 80, y: 25 }, { x: 50, y: 60 },
    { x: 30, y: 70 }, { x: 70, y: 45 }, { x: 15, y: 50 },
    { x: 85, y: 65 }, { x: 45, y: 35 }
  ];
  
  return (
    <motion.button 
      onClick={onOpenTelepathy}
      className="w-full glass-panel p-4 border-cyan-500/20 bg-gradient-to-r from-cyan-500/5 via-violet-500/5 to-pink-500/5 overflow-hidden relative hover:border-cyan-500/40 transition-all group text-left"
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: 0.5 }}
      whileHover={{ scale: 1.02 }}
      whileTap={{ scale: 0.98 }}
      data-testid="telepathy-banner-btn"
    >
      <div className="flex items-center gap-4">
        {/* Mini Globe */}
        <div className="relative w-14 h-14 flex-shrink-0">
          <motion.div 
            className="absolute inset-0 rounded-full border border-cyan-500/30"
            style={{ boxShadow: '0 0 15px rgba(6, 182, 212, 0.2)' }}
            animate={{ rotate: 360 }}
            transition={{ duration: 20, repeat: Infinity, ease: 'linear' }}
          />
          <svg className="absolute inset-0 w-full h-full" viewBox="0 0 100 100">
            <ellipse cx="50" cy="50" rx="46" ry="46" fill="none" stroke="rgba(6, 182, 212, 0.15)" strokeWidth="0.5" />
            <ellipse cx="50" cy="50" rx="46" ry="20" fill="none" stroke="rgba(6, 182, 212, 0.15)" strokeWidth="0.5" />
            <ellipse cx="50" cy="50" rx="20" ry="46" fill="none" stroke="rgba(6, 182, 212, 0.15)" strokeWidth="0.5" />
            {connectionPoints.map((point, i) => (
              <motion.circle
                key={i}
                cx={point.x}
                cy={point.y}
                r="2"
                fill={i % 2 === 0 ? '#22d3ee' : '#a78bfa'}
                animate={{ opacity: [0.3, 1, 0.3] }}
                transition={{ duration: 2, repeat: Infinity, delay: i * 0.2 }}
              />
            ))}
          </svg>
        </div>
        
        {/* Text */}
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1">
            <span className="text-xs tracking-widest uppercase text-cyan-400 font-medium">
              {language === 'tr' ? 'Yeni' : 'New'}
            </span>
            <motion.span className="text-cyan-400" animate={{ opacity: [1, 0.5, 1] }} transition={{ duration: 1.5, repeat: Infinity }}>
              <Zap className="w-3 h-3" />
            </motion.span>
          </div>
          <h3 className="text-sm font-heading text-slate-100 group-hover:text-cyan-300 transition-colors">
            {language === 'tr' ? 'Telepati Modu' : 'Telepathy Mode'}
          </h3>
          <p className="text-xs text-slate-500">
            {language === 'tr' ? 'İki kişilik canlı sezgisel algı deneyimi' : 'Live two-player intuitive perception experience'}
          </p>
        </div>
        
        <div className="flex-shrink-0">
          <motion.div
            animate={{ x: [0, 4, 0] }}
            transition={{ duration: 1.5, repeat: Infinity }}
          >
            <Users className="w-5 h-5 text-cyan-400 group-hover:text-cyan-300 transition-colors" />
          </motion.div>
        </div>
      </div>
    </motion.button>
  );
};

// How It Works Carousel
const HowItWorksCarousel = () => {
  const [currentSlide, setCurrentSlide] = useState(0);
  const [touchStart, setTouchStart] = useState(null);
  const { language } = useLanguage();
  
  const slides = language === 'tr' ? [
    { title: 'Kart Oluşturma', desc: 'Kartlar testin başında rastgele oluşturulur.', icon: '🎴' },
    { title: 'Sembol Seçimi', desc: 'Sezgisel olarak algıladığınız sembolü seçin.', icon: '👁️' },
    { title: 'Sonuç', desc: 'Kart açılır ve bilip bilmediğinizi gösterir.', icon: '✨' },
    { title: 'İlerleme', desc: 'Tekrarlanan testlerle sonuçlar netleşir.', icon: '📊' },
  ] : [
    { title: 'Card Generation', desc: 'Cards are randomly generated at the start of the test.', icon: '🎴' },
    { title: 'Symbol Selection', desc: 'Select the symbol you intuitively perceive.', icon: '👁️' },
    { title: 'Result', desc: 'Card reveals whether you were right.', icon: '✨' },
    { title: 'Progress', desc: 'Results clarify with repeated tests.', icon: '📊' },
  ];
  
  const nextSlide = () => setCurrentSlide((prev) => (prev + 1) % slides.length);
  const prevSlide = () => setCurrentSlide((prev) => (prev - 1 + slides.length) % slides.length);
  
  return (
    <div className="glass-panel p-4 relative overflow-hidden border-violet-500/10">
      <h2 className="text-xs tracking-widest uppercase text-violet-400 font-medium mb-2 text-center flex items-center justify-center gap-2">
        <Sparkles className="w-3 h-3" />
        {language === 'tr' ? 'Nasıl Çalışır?' : 'How It Works?'}
      </h2>
      
      <div 
        className="relative h-16"
        onTouchStart={(e) => setTouchStart(e.touches[0].clientX)}
        onTouchEnd={(e) => {
          if (!touchStart) return;
          const diff = touchStart - e.changedTouches[0].clientX;
          if (Math.abs(diff) > 50) diff > 0 ? nextSlide() : prevSlide();
          setTouchStart(null);
        }}
      >
        <AnimatePresence mode="wait">
          <motion.div
            key={currentSlide}
            initial={{ opacity: 0, x: 50 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -50 }}
            transition={{ duration: 0.3 }}
            className="absolute inset-0 flex flex-col items-center justify-center text-center px-6"
          >
            <span className="text-lg mb-1">{slides[currentSlide].icon}</span>
            <h3 className="text-sm font-heading text-slate-100">{slides[currentSlide].title}</h3>
            <p className="text-slate-400 text-xs">{slides[currentSlide].desc}</p>
          </motion.div>
        </AnimatePresence>
      </div>
      
      <div className="flex items-center justify-center gap-1.5 mt-2">
        {slides.map((_, index) => (
          <button
            key={index}
            onClick={() => setCurrentSlide(index)}
            className={`h-1 rounded-full transition-all ${index === currentSlide ? 'w-3 bg-violet-400' : 'w-1 bg-white/20'}`}
          />
        ))}
      </div>
    </div>
  );
};

export const Landing = ({ onBeginSession, onOpenTelepathy, onOpenMeditation, onOpenTrainingHub, onOpenFeedback, onOpenAbout }) => {
  const { isMuted, startSound, toggleMute } = useAmbientSound();
  const { language, t } = useLanguage();
  const [playerName, setPlayerName] = useState('');
  const [deckSize, setDeckSize] = useState(25);
  
  useEffect(() => {
    startSound();
    const handleFirstInteraction = () => startSound();
    document.addEventListener('click', handleFirstInteraction, { once: true });
    document.addEventListener('touchstart', handleFirstInteraction, { once: true });
    return () => {
      document.removeEventListener('click', handleFirstInteraction);
      document.removeEventListener('touchstart', handleFirstInteraction);
    };
  }, [startSound]);
  
  const handleSubmit = (e) => {
    e.preventDefault();
    onBeginSession(playerName || null, deckSize);
  };
  
  return (
    <div className="min-h-screen flex items-center justify-center p-4 md:p-8 relative overflow-hidden" data-testid="landing-screen">
      <MysticalParticles />
      <SoundToggle isMuted={isMuted} onToggle={toggleMute} />
      
      {/* Active Users Counter - Top Center */}
      <div className="fixed top-4 left-1/2 -translate-x-1/2 z-50">
        <ActiveUsersCounter />
      </div>
      
      {/* Language Toggle - Top Right, below nav buttons */}
      <div className="fixed top-[4.25rem] right-4 sm:top-[5.25rem] sm:right-6 z-50">
        <LanguageToggle />
      </div>
      
      <motion.div
        className="max-w-xl w-full space-y-6 relative z-10"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 1 }}
      >
        {/* Title with Original Aura */}
        <div className="text-center space-y-4 relative py-16">
          <AuraRing />
          <div className="relative z-10">
            <h1 className="font-heading text-glow-strong">
              <span className="block text-5xl md:text-6xl lg:text-7xl text-slate-50 mb-2">ZENER</span>
              <span className="block text-4xl md:text-5xl lg:text-6xl text-violet-300/90">MIND</span>
            </h1>
          </div>
          <p className="text-slate-400 text-sm leading-relaxed max-w-sm mx-auto relative z-10 font-mystic-alt">
            {language === 'tr' ? 'Sezgisel yeteneklerini test et, üçüncü gözünü aç' : 'Test your intuitive abilities, open your third eye'}
          </p>
        </div>
        
        {/* Form */}
        <form onSubmit={handleSubmit} className="space-y-5">
          {/* Name Input */}
          <div>
            <input
              type="text"
              value={playerName}
              onChange={(e) => setPlayerName(e.target.value)}
              placeholder={language === 'tr' ? 'İsminiz' : 'Your name'}
              className="w-full bg-transparent border-b border-white/20 px-0 py-2 text-lg text-center text-white placeholder:text-white/30 focus:border-violet-500 focus:outline-none transition-colors font-heading"
              maxLength={30}
              data-testid="player-name-input"
            />
          </div>
          
          {/* Deck Size */}
          <div className="space-y-2">
            <label className="block text-xs tracking-widest uppercase text-slate-500 text-center">
              {language === 'tr' ? 'Kart Sayısı' : 'Card Count'}
            </label>
            <div className="grid grid-cols-4 gap-2">
              {[5, 25, 50, 100].map((size) => (
                <button
                  key={size}
                  type="button"
                  onClick={() => setDeckSize(size)}
                  className={`py-2 rounded-2xl text-sm font-medium transition-all border ${
                    deckSize === size
                      ? 'bg-violet-500/20 border-violet-500/50 text-violet-300 shadow-[0_0_15px_rgba(139,92,246,0.3)]'
                      : 'bg-white/5 border-white/10 text-slate-400 hover:bg-white/10'
                  }`}
                  data-testid={`deck-size-${size}`}
                >
                  {size}
                </button>
              ))}
            </div>
          </div>
          
          {/* Start Button */}
          <div className="flex justify-center pt-2">
            <button
              type="submit"
              className="relative overflow-hidden rounded-full bg-gradient-to-r from-violet-500/20 to-cyan-500/20 px-10 py-3 text-sm font-medium text-white transition-all hover:from-violet-500/30 hover:to-cyan-500/30 hover:shadow-[0_0_30px_rgba(139,92,246,0.4)] active:scale-95 border border-violet-500/30 flex items-center gap-2"
              data-testid="begin-session-btn"
            >
              <Moon className="w-4 h-4 text-violet-400" />
              {language === 'tr' ? 'Teste Başla' : 'Start Test'}
            </button>
          </div>
        </form>
        
        {/* Action Buttons Grid */}
        <div className="grid grid-cols-2 gap-3">
          {/* Meditation Button */}
          <motion.button
            onClick={onOpenMeditation}
            className="glass-panel p-4 border-cyan-500/20 bg-gradient-to-br from-cyan-500/5 to-teal-500/5 hover:border-cyan-500/40 transition-all group text-left"
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
            data-testid="meditation-btn"
          >
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-cyan-500/20 flex items-center justify-center">
                <Eye className="w-5 h-5 text-cyan-400" />
              </div>
              <div>
                <h3 className="text-sm font-heading text-slate-100 group-hover:text-cyan-300 transition-colors">
                  {language === 'tr' ? '3. Göz Meditasyonu' : '3rd Eye Meditation'}
                </h3>
                <p className="text-xs text-slate-500">
                  {language === 'tr' ? 'Paylaşımlı odaklanma' : 'Shared focus'}
                </p>
              </div>
            </div>
          </motion.button>
          
          {/* Training Hub Button */}
          <motion.button
            onClick={onOpenTrainingHub}
            className="glass-panel p-4 border-amber-500/20 bg-gradient-to-br from-amber-500/5 to-orange-500/5 hover:border-amber-500/40 transition-all group text-left"
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
            data-testid="training-hub-btn"
          >
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-amber-500/20 flex items-center justify-center">
                <Palette className="w-5 h-5 text-amber-400" />
              </div>
              <div>
                <h3 className="text-sm font-heading text-slate-100 group-hover:text-amber-300 transition-colors">
                  {language === 'tr' ? 'Eğitim Merkezi' : 'Training Center'}
                </h3>
                <p className="text-xs text-slate-500">
                  {language === 'tr' ? 'Renk, Rakam, Telepati' : 'Color, Number, Telepathy'}
                </p>
              </div>
            </div>
          </motion.button>
        </div>
        
        {/* Telepathy Banner */}
        <TelepathyBanner onOpenTelepathy={onOpenTelepathy} />
        
        {/* How It Works */}
        <HowItWorksCarousel />
        
        {/* Footer Links */}
        <div className="flex justify-center gap-6 pt-4">
          <button
            onClick={onOpenFeedback}
            className="text-slate-500 hover:text-violet-400 text-sm transition-colors"
            data-testid="feedback-link"
          >
            {language === 'tr' ? 'Geri Bildirim' : 'Feedback'}
          </button>
          <span className="text-slate-700">•</span>
          <button
            onClick={onOpenAbout}
            className="text-slate-500 hover:text-violet-400 text-sm transition-colors"
            data-testid="about-link"
          >
            {language === 'tr' ? 'Geliştirici Hakkında' : 'About Developer'}
          </button>
        </div>
        
        {/* Footer decorations */}
        <div className="flex justify-center gap-4 opacity-30">
          <Stars className="w-4 h-4 text-violet-400" />
          <Moon className="w-4 h-4 text-cyan-400" />
          <Sparkles className="w-4 h-4 text-pink-400" />
        </div>
        
        <div className="text-center">
          <p className="text-slate-600 text-xs">
            {language === 'tr' ? 'İçgüdülerinize güvenin' : 'Trust your instincts'}
          </p>
        </div>
      </motion.div>
    </div>
  );
};
